import { useCallback, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChatTranscript } from '@/features/votes/chat';
import type {
  ActionCardContent,
  MeetingAssistAutoReview,
  MeetingAssistCard,
  MeetingAssistCardAsSeen,
  MeetingAssistDetails,
} from '@/features/votes/entity-votes';

function formatTime(dateString: string) {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function isEmpty(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function formatFieldValue(value: unknown): string {
  if (isEmpty(value)) return '(empty)';
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string'))
    return value.join(', ');
  return JSON.stringify(value, null, 2);
}

function ContentFields({
  content,
  fields,
}: {
  content: ActionCardContent;
  fields: string[];
}) {
  return (
    <dl className="space-y-2">
      {fields.map((field) => (
        <div key={field}>
          <dt className="text-xs text-muted-foreground">{field}</dt>
          <dd className="whitespace-pre-wrap break-words">
            {formatFieldValue(content[field])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function CardTimeline({
  createdAt,
  asSeen,
}: {
  createdAt: string;
  asSeen: MeetingAssistCardAsSeen | null;
}) {
  const events = [
    { label: 'Made', at: createdAt },
    { label: 'Revised', at: asSeen?.revised_at },
    { label: 'Resolved', at: asSeen?.resolved_at },
    { label: 'Dismissed', at: asSeen?.dismissed_at },
  ].flatMap(({ label, at }) => (at ? [{ label, at }] : []));
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {events.map((event) => (
        <span key={event.label}>
          {event.label} {formatTime(event.at)}
        </span>
      ))}
    </div>
  );
}

export function AutoReviewVerdict({
  review,
}: {
  review: MeetingAssistAutoReview;
}) {
  return (
    <p className="text-xs text-muted-foreground">
      Auto-review:{' '}
      {review.review_failed
        ? 'failed'
        : review.needs_approval
          ? 'needs approval'
          : 'no approval needed'}
      {review.risk_level && `, risk ${review.risk_level}`}
    </p>
  );
}

export function AsSeenDetails({ asSeen }: { asSeen: MeetingAssistCardAsSeen }) {
  // The header already shows the type and title, and empty fields say nothing
  const fields = Object.keys(asSeen.content).filter(
    (field) =>
      field !== 'type' &&
      !(field === 'title' && asSeen.content.title === asSeen.title) &&
      !isEmpty(asSeen.content[field])
  );
  const original = asSeen.original_content;
  const changedFields = original
    ? [...new Set([...Object.keys(original), ...Object.keys(asSeen.content)])]
        .filter((field) => field !== 'type')
        .filter(
          (field) =>
            JSON.stringify(original[field]) !==
            JSON.stringify(asSeen.content[field])
        )
    : [];
  const hasOutcome = asSeen.result !== null || asSeen.external_ref !== null;

  return (
    <>
      <ContentFields content={asSeen.content} fields={fields} />

      {original && changedFields.length > 0 && (
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            Agent's version of the changed fields
          </summary>
          <div className="mt-2 p-3 rounded-md bg-background/60">
            <ContentFields content={original} fields={changedFields} />
          </div>
        </details>
      )}

      {asSeen.dismiss_feedback && (
        <p className="text-muted-foreground">
          Dismissed because: {asSeen.dismiss_feedback}
        </p>
      )}

      {hasOutcome && (
        <details>
          <summary className="cursor-pointer text-muted-foreground">
            Outcome
          </summary>
          <pre className="mt-2 p-3 rounded-md bg-background/60 text-xs whitespace-pre-wrap break-words">
            {JSON.stringify(
              { result: asSeen.result, external_ref: asSeen.external_ref },
              null,
              2
            )}
          </pre>
        </details>
      )}
    </>
  );
}

function SuggestionCard({
  card,
  onShowInChat,
}: {
  card: MeetingAssistCard;
  onShowInChat: (messageId: string) => void;
}) {
  const asSeen = card.as_seen;
  const messageId = card.assistant_message_id;

  return (
    <li className="p-3 rounded-md bg-muted/50 text-sm space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">
          #{card.local_id}
        </span>
        <Badge variant="outline">{card.action_type}</Badge>
        {asSeen && <span className="font-medium">{asSeen.title}</span>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {card.importance !== null && (
            <Badge variant="outline">Importance {card.importance}/10</Badge>
          )}
          {asSeen?.original_content && (
            <Badge variant="outline">Differs from the agent's version</Badge>
          )}
          {asSeen?.agent_action && (
            <Badge variant="outline">{asSeen.agent_action}</Badge>
          )}
          <Badge variant="secondary">
            {asSeen ? asSeen.status : 'Changed after the vote'}
          </Badge>
        </div>
      </div>

      <CardTimeline createdAt={card.created_at} asSeen={asSeen} />

      {card.context && (
        <div>
          <p className="text-xs text-muted-foreground">Why the agent made it</p>
          <p className="whitespace-pre-wrap">{card.context}</p>
        </div>
      )}

      {asSeen ? (
        <AsSeenDetails asSeen={asSeen} />
      ) : (
        // No card keeps its earlier versions, so a card that changed after
        // the vote shows only the fields set when it was made
        <p className="text-muted-foreground">
          Its content and status are hidden. Show in chat finds the tool call
          that made it.
        </p>
      )}

      {card.auto_review && <AutoReviewVerdict review={card.auto_review} />}

      {messageId && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onShowInChat(messageId)}
        >
          Show in chat
        </Button>
      )}
    </li>
  );
}

export default function MeetingAssistDetailsCard({
  details,
}: {
  details: MeetingAssistDetails;
}) {
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const highlightedMessage = useRef<HTMLDivElement | null>(null);

  // The transcript mounts the highlighted message's ref when the highlight
  // moves to it, so this scrolls to each newly highlighted message
  const scrollToHighlighted = useCallback((message: HTMLDivElement | null) => {
    highlightedMessage.current = message;
    message?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const showInChat = (messageId: string) => {
    if (messageId === highlightedMessageId) {
      highlightedMessage.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      setHighlightedMessageId(messageId);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Meeting Assist</CardTitle>
        <CardDescription>
          {details.meeting_name || 'Unnamed Meeting'}
          <span className="ml-2 text-xs font-mono">
            ({details.meeting_id.slice(0, 8)}...)
          </span>
        </CardDescription>
      </CardHeader>
      {details.chat_id === null ? (
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No suggestions turn ran for this meeting
          </p>
        </CardContent>
      ) : (
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Suggestions before the vote</h4>
            {details.cards.length > 0 ? (
              <ul className="space-y-2">
                {details.cards.map((card) => (
                  <SuggestionCard
                    key={card.local_id}
                    card={card}
                    onShowInChat={showInChat}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No suggestions were made before the vote
              </p>
            )}
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-sm">Suggestions Chat</h4>
            <p className="text-xs text-muted-foreground">
              Turns that ran after the vote are redacted
            </p>
            <div className="rounded-md border p-4">
              <ChatTranscript
                chatHistory={details.chat_history}
                highlightedMessageId={highlightedMessageId}
                highlightLabel="Made the card"
                highlightedMessageRef={scrollToHighlighted}
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
